import { generateUUID } from '@sveltia/utils/crypto';

import { slugify } from '$lib/services/common/slug';
import {
  handleDateTimeTag,
  handleFilePathTag,
  handleSlugTag,
  handleUuidTag,
} from '$lib/services/common/template/handlers';
import { processNestedTemplates } from '$lib/services/common/template/nested';
import { applyTransformations, parseTransformations } from '$lib/services/common/transformations';
import { getField } from '$lib/services/contents/entry/fields';
import { sanitizePath } from '$lib/services/utils/file';

/**
 * @import { FillTemplateOptions, GetFieldArgs } from '$lib/types/private';
 */

/**
 * @typedef {object} ReplaceSubContextBase
 * @property {string} identifierField Field name to identify the title.
 * @property {string | undefined} basePath Base path for the entry file.
 */

/**
 * @typedef {FillTemplateOptions & ReplaceSubContextBase} ReplaceSubContext
 */

/**
 * @typedef {object} ReplaceContext
 * @property {ReplaceSubContext} replaceSubContext Context for `replaceSub`.
 * @property {GetFieldArgs} getFieldArgs Arguments for `getField`.
 */

/**
 * Template tag replacer subroutine.
 * @internal
 * @param {string} tag Field name or special tag.
 * @param {ReplaceSubContext} context Replacement context.
 * @returns {any} Replaced value.
 */
export const replaceTemplateTag = (tag, context) => {
  const { type, content, entryFilePath, locale, dateTimeParts, basePath } = context;
  // Handle date-time fields. Parts are pre-calculated in `fillTemplate` to avoid redundant
  // calculations for multiple date-time tags in the same template.
  const _dateTimeParts = /** @type {Record<string, string>} */ (dateTimeParts);
  const dateTimeValue = handleDateTimeTag(tag, _dateTimeParts);

  if (dateTimeValue !== undefined) {
    return dateTimeValue;
  }

  // Handle slug tag
  const slugValue = handleSlugTag(tag, context);

  if (slugValue !== undefined) {
    return slugValue;
  }

  // Handle UUID tags
  const uuidValue = handleUuidTag(tag);

  if (uuidValue !== undefined) {
    return uuidValue;
  }

  // Handle locale tag for preview path
  if (type === 'preview_path' && tag === 'locale') {
    return locale;
  }

  // Handle file path related tags
  if (type === 'preview_path' || type === 'media_folder') {
    const filePathValue = handleFilePathTag(tag, entryFilePath, basePath);

    if (filePathValue !== undefined) {
      return filePathValue;
    }

    // `{{fields.*}}` tags are not supported in the media folder path and preview path templates, so
    // we return `undefined` instead of the field value to avoid generating invalid paths.
    return undefined;
  }

  // Handle field values
  return content[tag.replace(/^fields\./, '')];
};

/**
 * Template placeholder replacer.
 * @internal
 * @param {string} placeholder Field name or one of special tags. May contain transformations.
 * @param {ReplaceContext} context Context for replacement.
 * @returns {string} Replaced string.
 */
export const replaceTemplatePlaceholder = (placeholder, context) => {
  const { replaceSubContext, getFieldArgs } = context;
  const { value: tag, transformations: parsedTransformations } = parseTransformations(placeholder);
  let value = replaceTemplateTag(tag, replaceSubContext);

  // Process nested templates in transformation arguments
  const transformations = processNestedTemplates(parsedTransformations, (innerTag) =>
    String(replaceTemplateTag(innerTag, replaceSubContext) ?? ''),
  );

  // Fall back with a random ID unless the `default` transformation is defined
  const hasDefaultTransformation = parsedTransformations.some(
    (tf) => tf.args.defaultValue !== undefined,
  );

  const hasComplexNestedTemplateArgs = parsedTransformations.some((tf) =>
    [tf.args.defaultValue, tf.args.truthyValue, tf.args.falsyValue].some(
      (arg) => typeof arg === 'string' && arg.includes('{{') && !/^{{[^{}]+}}$/.test(arg),
    ),
  );

  if (value === undefined && !hasDefaultTransformation) {
    return generateUUID('short');
  }

  if (
    (value === undefined || value === '') &&
    hasDefaultTransformation &&
    hasComplexNestedTemplateArgs
  ) {
    return `${generateUUID('short')}-${generateUUID('short')}`;
  }

  const { type, locale } = replaceSubContext;

  if (transformations.length) {
    value = applyTransformations({
      fieldConfig: getField({ ...getFieldArgs, keyPath: tag }),
      value,
      transformations,
      locale,
    });
  }

  // Sanitize path traversal attempts first by removing `.` and `..` segments to prevent writing
  // outside the intended directory structure. This must happen before slugification to ensure that
  // inputs like `../foo` don’t become `..-foo` (with `/` converted to `-`).
  value = sanitizePath(String(value));

  // Return the value as-is when generating the preview path or media folder path
  if (type) {
    return value;
  }

  // Slugify the value for a slug or filename. Don’t limit the length here; it will be handled later
  // in `fillTemplate`.
  return slugify(value, { locale, maxLength: Infinity });
};
